// Phase 11 Step 7 — useOwlHourUserSlips hook.
//
// Read-only consumer of owl_hour_user_slips table. Fetches the
// authenticated user's Owl Hour selection history — stakes placed,
// results, legs correct/total, morning reveal state.
//
// Trust contract:
//   - Read via RLS-scoped SELECT. Writes via record_owl_hour_user_slip RPC.
//   - Defensive parsing — missing fields render with fallbacks.
//   - No localStorage. No external HTTP.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type { OwlTier, OwlResult } from "./useOwlHourSlips.ts";

// ─── Types ─────────────────────────────────────────────────────

export interface OwlUserSlip {
  id: string;
  owl_slip_id: string;
  tier: OwlTier;
  stake: number;
  potential_return: number | null;
  actual_return: number | null;
  result: OwlResult | null;
  legs_correct: number | null;
  legs_total: number | null;
  morning_notified_at: string | null;
  share_card_generated: boolean;
  meta: Record<string, unknown>;
  simulated: boolean;
  created_at: string;
  updated_at: string;
}

export interface UseOwlUserSlipsState {
  userSlips: readonly OwlUserSlip[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Row parser ────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseOwlUserSlip(row: Record<string, unknown>): OwlUserSlip {
  return {
    id: String(row.id ?? ""),
    owl_slip_id: String(row.owl_slip_id ?? ""),
    tier: (row.tier as OwlTier) ?? "conservative",
    stake: typeof row.stake === "number" ? row.stake : Number(row.stake ?? 0),
    potential_return: asNum(row.potential_return),
    actual_return: asNum(row.actual_return),
    result: asStr(row.result) as OwlResult | null,
    legs_correct: asNum(row.legs_correct) !== null
      ? Math.round(asNum(row.legs_correct)!)
      : null,
    legs_total: asNum(row.legs_total) !== null
      ? Math.round(asNum(row.legs_total)!)
      : null,
    morning_notified_at: asStr(row.morning_notified_at),
    share_card_generated: row.share_card_generated === true,
    meta:
      typeof row.meta === "object" && row.meta !== null
        ? (row.meta as Record<string, unknown>)
        : {},
    simulated: row.simulated === true,
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

// ─── Hook ──────────────────────────────────────────────────────

export function useOwlHourUserSlips(
  opts: {
    tierFilter?: OwlTier;
    limit?: number;
  } = {},
): UseOwlUserSlipsState {
  const { user } = useAuthSession();
  const [userSlips, setUserSlips] = useState<readonly OwlUserSlip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);
  const limit = opts.limit ?? 30;

  useEffect(() => {
    if (!user) {
      setUserSlips([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      let resp: {
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      };

      if (opts.tierFilter) {
        resp = await (
          client.from("owl_hour_user_slips" as never) as unknown as {
            select: (cols: string) => {
              eq: (col: string, val: string) => {
                order: (col: string, opts: { ascending: boolean }) => {
                  limit: (n: number) => Promise<{
                    data: Record<string, unknown>[] | null;
                    error: { message: string } | null;
                  }>;
                };
              };
            };
          }
        )
          .select("*")
          .eq("tier", opts.tierFilter)
          .order("created_at", { ascending: false })
          .limit(limit);
      } else {
        resp = await (
          client.from("owl_hour_user_slips" as never) as unknown as {
            select: (cols: string) => {
              order: (col: string, opts: { ascending: boolean }) => {
                limit: (n: number) => Promise<{
                  data: Record<string, unknown>[] | null;
                  error: { message: string } | null;
                }>;
              };
            };
          }
        )
          .select("*")
          .order("created_at", { ascending: false })
          .limit(limit);
      }

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setUserSlips([]);
        setLoading(false);
        return;
      }

      setUserSlips(
        (resp.data ?? [])
          .filter((r): r is Record<string, unknown> => r !== null)
          .map(parseOwlUserSlip),
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, opts.tierFilter, limit, tick]);

  return useMemo(
    () => ({ userSlips, loading, error, refetch }),
    [userSlips, loading, error, refetch],
  );
}
